import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import {
  LayoutDashboard, Users, Calendar, BookOpen, CreditCard,
  Star, Settings, Bell, BellRing, Plus, LogOut, Menu, Activity, MessageSquare, Gift, Package,
} from 'lucide-react';
import { format } from 'date-fns';
import { pushSupported, getExistingSubscription, subscribeToPush, unsubscribeFromPush } from './pushNotifications';

const navItems = [
  { to: '/admin/dashboard',   icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/admin/clients',     icon: Users,           label: 'Clients' },
  { to: '/admin/calendar',    icon: Calendar,        label: 'Calendar' },
  { to: '/admin/bookings',    icon: BookOpen,        label: 'Bookings' },
  { to: '/admin/payments',    icon: CreditCard,      label: 'Payments' },
  { to: '/admin/activities',  icon: Activity,        label: 'Activity' },
  { to: '/admin/winback',     icon: MessageSquare,   label: 'Win-Back' },
  { to: '/admin/raffle',      icon: Gift,            label: 'Raffle' },
  { to: '/admin/maintenance', icon: Star,            label: 'Maintenance' },
  { to: '/admin/inventory',   icon: Package,         label: 'Inventory' },
  { to: '/admin/settings',    icon: Settings,        label: 'Settings' },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!pushSupported()) return;
    getExistingSubscription().then((sub) => setPushOn(!!sub)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin');
  };

  const handleToggleNotifications = async () => {
    if (!pushSupported()) {
      alert('Push notifications aren\'t supported in this browser. On iPhone, open this app from the Home Screen icon (not Safari) and try again.');
      return;
    }
    setPushBusy(true);
    try {
      if (pushOn) {
        await unsubscribeFromPush();
        setPushOn(false);
      } else {
        await subscribeToPush();
        setPushOn(true);
      }
    } catch (err) {
      alert(err.message || 'Could not update notification settings.');
    } finally {
      setPushBusy(false);
    }
  };

  const NavItem = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-sm font-medium ${
          isActive
            ? 'bg-[#C9A84C] text-black'
            : 'text-gray-400 hover:text-white hover:bg-white/10'
        }`
      }
    >
      <Icon size={18} />
      <span>{label}</span>
    </NavLink>
  );

  return (
    <div className="flex h-screen bg-[#F5F5F0] text-gray-900 overflow-hidden">
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-30 w-64 bg-[#0D0D0D] flex flex-col transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
      >
        {/* Logo */}
        <div className="px-6 py-5 border-b border-white/10">
          <h1 className="text-white font-bold text-lg tracking-wide">Monarch</h1>
          <p className="text-[#C9A84C] text-xs mt-0.5">Admin Dashboard</p>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map(item => <NavItem key={item.to} {...item} />)}
        </nav>

        {/* Logout */}
        <div className="px-3 pb-4 border-t border-white/10 pt-4">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors text-sm font-medium w-full"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top header */}
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden text-gray-600 hover:text-gray-900"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={22} />
            </button>
            <span className="text-gray-500 text-sm hidden sm:block">
              {format(new Date(), 'EEEE, MMMM d, yyyy')}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleToggleNotifications}
              disabled={pushBusy}
              title={pushOn ? 'Notifications on — click to turn off' : 'Turn on push notifications'}
              className={`relative transition-colors disabled:opacity-50 ${pushOn ? 'text-[#68caf1]' : 'text-gray-500 hover:text-gray-900'}`}
            >
              {pushOn ? <BellRing size={20} /> : <Bell size={20} />}
            </button>
            <NavLink
              to="/admin/bookings"
              className="flex items-center gap-2 bg-[#C9A84C] text-black px-3 py-1.5 rounded-lg text-sm font-semibold hover:bg-[#b8943d] transition-colors"
            >
              <Plus size={16} />
              New Booking
            </NavLink>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#0D0D0D] border-t border-white/10 z-20 flex justify-around py-2">
        {navItems.slice(0, 6).map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 px-1.5 py-1 rounded text-[10px] ${
                isActive ? 'text-[#C9A84C]' : 'text-gray-500'
              }`
            }
          >
            <Icon size={18} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
